import { getBackendUrl } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight, 
  Loader2, 
  CheckCircle, 
  ClipboardList,
  User,
  Mail,
  Phone,
  Send
} from 'lucide-react';
import SeoHead from '../components/SeoHead';

const PublicQualificationPage = () => {
  const { formType } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Candidate info
  const [candidateInfo, setCandidateInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  
  // Form navigation
  const [currentStep, setCurrentStep] = useState(0); // 0 = info, 1+ = questions
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    fetchForm();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType]);

  const fetchForm = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/public/qualification/forms/${formType}`);
      if (response.ok) {
        const data = await response.json();
        setForm(data);
      } else {
        toast.error('Formulaire non trouvé');
      }
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Erreur lors du chargement du formulaire');
    } finally {
      setLoading(false);
    }
  };

  const handleCandidateInfoChange = (field, value) => {
    setCandidateInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const validateCandidateInfo = () => {
    if (!candidateInfo.first_name.trim()) {
      toast.error('Veuillez entrer votre prénom');
      return false;
    }
    if (!candidateInfo.last_name.trim()) {
      toast.error('Veuillez entrer votre nom');
      return false;
    }
    if (!candidateInfo.email.trim() || !candidateInfo.email.includes('@')) {
      toast.error('Veuillez entrer un email valide');
      return false;
    }
    if (!candidateInfo.phone.trim()) {
      toast.error('Veuillez entrer votre numéro de téléphone');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 0) {
      if (!validateCandidateInfo()) return;
    } else {
      const currentQuestion = form.questions[currentStep - 1];
      if (!answers[currentQuestion.id]?.trim()) {
        toast.error('Veuillez répondre à cette question');
        return;
      }
    }
    setCurrentStep(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    // Validate last answer
    const lastQuestion = form.questions[form.questions.length - 1];
    if (!answers[lastQuestion.id]?.trim()) {
      toast.error('Veuillez répondre à cette question');
      return;
    }

    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([question_id, answer]) => ({
        question_id,
        answer
      }));

      const response = await fetch(`${getBackendUrl()}/api/public/qualification/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_type: formType,
          first_name: candidateInfo.first_name,
          last_name: candidateInfo.last_name,
          email: candidateInfo.email,
          phone: candidateInfo.phone,
          answers: formattedAnswers
        })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la soumission');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const getProgress = () => {
    if (!form) return 0;
    const totalSteps = form.questions.length + 1; // +1 for candidate info
    return Math.round((currentStep / totalSteps) * 100);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'cognitive': 'Cognitif',
      'social': 'Social',
      'execution': 'Exécution',
      'mental': 'Résolution'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tp-primary" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-semibold mb-4">Formulaire non trouvé</h2>
          <p className="text-gray-600 mb-4">Ce formulaire de qualification n'existe pas.</p>
          <Link to="/">
            <Button>Retour à l'accueil</Button>
          </Link>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <SeoHead pageTitle={`${form.title} | Talent Partner`} />
        <Card className="p-8 text-center max-w-lg">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-display font-semibold text-gray-900 mb-4">
            Merci pour votre candidature !
          </h2>
          <p className="text-gray-600 mb-6">
            Votre évaluation a été soumise avec succès. Notre équipe va analyser votre profil 
            et reviendra vers vous très prochainement.
          </p>
          <div className="space-y-3">
            <Link to="/">
              <Button className="w-full bg-tp-primary hover:bg-tp-primary-light">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SeoHead pageTitle={`${form.title} | Talent Partner`} />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-tp-primary font-display font-semibold">
              <span className="text-xl">Talent Partner</span>
            </Link>
            <div className="text-sm text-gray-500">
              {currentStep === 0 ? 'Informations' : `Question ${currentStep}/${form.questions.length}`}
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4">
          <Progress value={getProgress()} className="h-1" />
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Title Card */}
        {currentStep === 0 && (
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-tp-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="h-8 w-8 text-tp-primary" />
            </div>
            <h1 className="text-2xl md:text-3xl font-display font-semibold text-gray-900 mb-3">
              {form.title}
            </h1>
            <p className="text-gray-600 max-w-xl mx-auto">
              {form.description}
            </p>
            <p className="text-sm text-gray-500 mt-4">
              {form.questions.length} questions • Durée estimée : 15-20 minutes
            </p>
          </div>
        )}

        <Card className="p-6 md:p-8">
          {/* Step 0: Candidate Info */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <User className="h-5 w-5 text-tp-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Vos informations</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom *</Label>
                  <Input
                    id="first_name"
                    value={candidateInfo.first_name}
                    onChange={(e) => handleCandidateInfoChange('first_name', e.target.value)}
                    placeholder="Votre prénom"
                    data-testid="input-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom *</Label>
                  <Input
                    id="last_name"
                    value={candidateInfo.last_name}
                    onChange={(e) => handleCandidateInfoChange('last_name', e.target.value)}
                    placeholder="Votre nom"
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={candidateInfo.email}
                  onChange={(e) => handleCandidateInfoChange('email', e.target.value)}
                  placeholder="votre.email@exemple.com"
                  data-testid="input-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Téléphone *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={candidateInfo.phone}
                  onChange={(e) => handleCandidateInfoChange('phone', e.target.value)}
                  placeholder="+261 34 00 000 00"
                  data-testid="input-phone"
                />
              </div>

              <div className="pt-4">
                <Button 
                  onClick={handleNext}
                  className="w-full bg-tp-primary hover:bg-tp-primary-light"
                  data-testid="btn-start"
                >
                  Commencer l'évaluation
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Question Steps */}
          {currentStep > 0 && currentStep <= form.questions.length && (
            <div className="space-y-6">
              {(() => {
                const question = form.questions[currentStep - 1];
                return (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-tp-primary bg-tp-primary/10 px-3 py-1 rounded-full">
                        {getCategoryLabel(question.category)}
                      </span>
                      <span className="text-sm text-gray-500">
                        Question {currentStep} sur {form.questions.length}
                      </span>
                    </div>

                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">
                        {question.question}
                      </h2>
                      <Textarea
                        value={answers[question.id] || ''}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder={question.placeholder}
                        rows={6}
                        className="resize-none"
                        data-testid={`answer-${question.id}`}
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Prenez le temps de répondre de manière détaillée. La qualité de vos réponses est importante.
                      </p>
                    </div>

                    <div className="flex justify-between pt-4">
                      <Button 
                        variant="outline" 
                        onClick={handlePrevious}
                        data-testid="btn-previous"
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Précédent
                      </Button>

                      {currentStep < form.questions.length ? (
                        <Button 
                          onClick={handleNext}
                          className="bg-tp-primary hover:bg-tp-primary-light"
                          data-testid="btn-next"
                        >
                          Suivant
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      ) : (
                        <Button 
                          onClick={handleSubmit}
                          disabled={submitting}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="btn-submit"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Soumettre ma candidature
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </Card>

        {/* Progress Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>Vos réponses sont enregistrées automatiquement.</p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12 py-6">
        <div className="max-w-3xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} Talent Partner. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicQualificationPage;
